//* Importing useState and useCallback to React
import React, { useState } from "react";
import { useAnswerSheets } from "../../../../../hooks/applications/answerSheets/useAnswerSheets";
import { useApplications } from "../../../../../hooks/applications/useApplications";

//* Importing the useNavigate hook from React Router
import { useNavigate,  } from "react-router-dom";

//* Importando o componente de Attention
import { Attention } from "../../../../../component/base/Attention";
import { ErrorMessages } from "../../../../../component/common/ErrorMessages";

// ? Importing types
import {
  CandidateFields,
  InputFieldProps,
  CandidateFormProps,
} from "./types/types";
import { CreateAnswerSheetInput } from "../../../../../hooks/applications/answerSheets/types/types";

//todo importando o component
import { Loading } from "../../../../../component/common/Loading";

const InputField: React.FC<InputFieldProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
}) => (
  <div className="mb-3" data-testid={`input-field-${id}`}>
    <label htmlFor={id} className="form-label" data-testid={`label-${id}`}>
      {label} <Attention />
    </label>
    <input
      id={id}
      name={id}
      type="text"
      className="form-control"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`input-${id}`}
    />
  </div>
);

const CandidateForm: React.FC<CandidateFormProps> = ({
  fields,
  setFields,
  editMode,
  testId,
  answerSheetId,
  token,
}) => {
  const navigate = useNavigate();

  const {
    createAnswerSheet,
    isCreating,
    updateAnswerSheet,
    isUpdating,
    getStatusJob,
  } = useAnswerSheets(token || "", testId);

  const { generateDiagram, isGeneratingDiagram } = useApplications(token || "");

  const [errors, setErrors] = useState<
    Partial<Record<keyof CandidateFields, string>>
  >({});
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  
  const clearErrorMessages = () => {
    setErrorMessages([]);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFields({
      ...fields,
      [name]: value,
    });

    if (name === "personType") {
      if (value !== "Aluno" && value !== "Candidato") {
        setErrors((prev) => ({
          ...prev,
          [name]: "Tipo de Pessoa deve ser Aluno ou Candidato.",
        }));
      } else {
        setErrors((prev) => {
          const { [name as keyof CandidateFields]: removed, ...rest } = prev;
          return rest;
        });
      }
    } else {
      if (value.trim() === "") {
        setErrors((prev) => ({ ...prev, [name]: "Este campo é obrigatório." }));
      } else {
        setErrors((prev) => {
          const { [name as keyof CandidateFields]: removed, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const validateAll = (): boolean => {
    let valid = true;
    const newErrors: Partial<Record<keyof CandidateFields, string>> = {};
    const keysToValidate = [
      "contestTitle",
      "contestSubtitle",
      "noticeLabel",
      "positionLabel",
      "positionName",
      "locationLabel",
      "locationName",
      "cityLabel",
      "cityName",
      "roomLabel",
      "room",
      "numberControlLabel",
      "personType",
    ] as Array<keyof CandidateFields>;

    keysToValidate.forEach((key) => {
      if (key === "personType") {
        if (fields[key] !== "Aluno" && fields[key] !== "Candidato") {
          valid = false;
          newErrors[key] = "Tipo de Pessoa deve ser Aluno ou Candidato.";
        }
      } else if (!fields[key] || fields[key].trim() === "") {
        valid = false;
        newErrors[key] = "Este campo é obrigatório.";
      }
    });

    setErrors(newErrors);
    return valid;
  };

  // Importante: usa o testId recebido via prop, e não o fields.testId
  const payload: CreateAnswerSheetInput = {
    diagram_id: fields.diagramId,
    application_id: testId, // usa o testId direto
    contest_title: fields.contestTitle,
    contest_subtitle: fields.contestSubtitle,
    notice_label: fields.noticeLabel,
    position_label: fields.positionLabel,
    position_name: fields.positionName,
    location_label: fields.locationLabel,
    location_name: fields.locationName,
    city_label: fields.cityLabel,
    city_name: fields.cityName,
    room_label: fields.roomLabel,
    number_control: fields.numberControl,
    person_type: fields.personType,
  };

  const openPdfWindowWithAlert = (): Window | null => {
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Gerando Folha-Resposta...</title>
          </head>
          <body>
            <p>A Folha-Resposta está sendo gerado. Por favor, não feche esta janela.</p>
            <script>
              window.addEventListener('beforeunload', function(e) {
                e.preventDefault();
                e.returnValue = 'A geração da Folha-Resposta ainda está em andamento. Tem certeza que deseja sair?';
              });
            </script>
          </body>
        </html>
      `);
      newWindow.document.close();
    }
    return newWindow;
  };

  const startPolling = (jobId: string, pdfWindow: Window | null) => {
    setIsPolling(true);
    const intervalId = setInterval(async () => {
      try {
        const statusResponse = await getStatusJob(jobId);
        if (statusResponse.job_status === "completed") {
          clearInterval(intervalId);
          setIsPolling(false);
          if (statusResponse.pdf_url) {
            if (pdfWindow) {
              pdfWindow.onbeforeunload = null;
              pdfWindow.document.open();
              pdfWindow.document.write(`
                <html>
                  <head>
                    <title>Folha-Resposta Gerada</title>
                  </head>
                  <body>
                    <h1>Folha-Resposta Gerada com Sucesso!</h1>
                    <p>Clique no link abaixo para visualizar a Folha-Resposta:</p>
                    <a href="${statusResponse.pdf_url}" target="_blank">Visualizar PDF</a>
                  </body>
                </html>
              `);
              pdfWindow.document.close();
            } else {
              window.open(statusResponse.pdf_url, "_blank");
            }
            navigate(`/tests/view/${testId}?tab=3`);
          } else {
            console.error("PDF URL não encontrado.");
          }
        } else if (statusResponse.job_status === "failed") {
          clearInterval(intervalId);
          setIsPolling(false);
          console.error("A geração do PDF falhou.");
        }
      } catch (err) {
        clearInterval(intervalId);
        setIsPolling(false);
        console.error("Erro ao fazer polling do job:", err);
      }
    }, 3000);
  };

 const handleSubmitAndPrint = async () => {
  if (!validateAll()) return;
  
  const pdfWindow = openPdfWindowWithAlert();

  try {
    const response: any = await createAnswerSheet(payload);
    const newAnswerSheetId = response?.id;

    if (!newAnswerSheetId) {
      console.error("ID da folha de resposta não retornado.");
      return;
    }

    const diagramResponse: any = await generateDiagram({
      id: testId,
      diagramId: fields.diagramId,
      answerSheetsId: newAnswerSheetId,
    });

    const jobId = diagramResponse.diagram_generation_id;

    if (!jobId) {
      console.error("ID do job de geração não retornado.");
      return;
    }

    startPolling(jobId, pdfWindow);

  } catch (error: any) {
    if (pdfWindow) {
      pdfWindow.close();
    }
    setErrorMessages([
      "Erro ao criar a folha de resposta. Verifique se a aplicação possui estudantes cadastrados."
    ]);
    
  }
};


  const handleUpdateAndPrint = async () => {
    if (!validateAll()) return;
    if (answerSheetId) {
      const pdfWindow = openPdfWindowWithAlert();
      try {
        await updateAnswerSheet({
          id: answerSheetId,
          answerSheetData: payload,
        });
        const diagramResponse: any = await generateDiagram({
          id: testId,
          diagramId: fields.diagramId,
          answerSheetsId: answerSheetId,
        });
        const jobId = diagramResponse.diagram_generation_id;
        if (!jobId) {
          console.error("ID do job de geração não retornado.");
          return;
        }
        startPolling(jobId, pdfWindow);
      } catch (error: any) {
        // Fecha a janela do PDF se ocorrer um erro
        if (pdfWindow) {
          pdfWindow.close();
        }
        
        setErrorMessages([
          "Erro ao criar a folha de resposta. Verifique se a aplicação possui estudantes cadastrados.",
        ]);
        
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateAll()) return;
    try {
      await createAnswerSheet(payload);
      navigate(`/tests/view/${testId}?tab=3`);
    } catch (error) {
      console.error("Erro ao criar a folha de resposta:", error);
    }
  };

  const handleUpdate = async () => {
    if (!validateAll()) return;
    if (answerSheetId) {
      try {
        await updateAnswerSheet({
          id: answerSheetId,
          answerSheetData: payload,
        });
        navigate(`/tests/view/${testId}?tab=3`);
      } catch (error) {
        console.error("Erro ao atualizar a folha de resposta:", error);
      }
    }
  };

  const inputConfigs = [
    {
      id: "contestTitle",
      label: "Título do Concurso",
      placeholder: "Digite o título do concurso",
    },
    {
      id: "contestSubtitle",
      label: "Subtítulo do Concurso",
      placeholder: "Digite o subtítulo do concurso",
    },
    {
      id: "noticeLabel",
      label: "Edital",
      placeholder: "Digite o rótulo do edital",
    },
    {
      id: "positionLabel",
      label: "Rótulo do Cargo",
      placeholder: "Digite o rótulo do cargo",
    },
    {
      id: "positionName",
      label: "Nome do Cargo",
      placeholder: "Digite o nome do cargo",
    },
    {
      id: "locationLabel",
      label: "Rótulo do Local",
      placeholder: "Digite o rótulo do local",
    },
    {
      id: "locationName",
      label: "Nome do Local",
      placeholder: "Digite o nome do local",
    },
    {
      id: "cityLabel",
      label: "Rótulo da Cidade",
      placeholder: "Digite o rótulo da cidade",
    },
    {
      id: "cityName",
      label: "Nome da Cidade",
      placeholder: "Digite o nome da cidade",
    },
    {
      id: "roomLabel",
      label: "Rótulo da Sala",
      placeholder: "Digite o rótulo da sala",
    },
    { id: "room", label: "Nome da Sala", placeholder: "Digite o nome da sala" },
    {
      id: "numberControlLabel",
      label: "Rótulo do Número de Controle",
      placeholder: "Digite o rótulo",
    },
  ] as const;

  if (isPolling) {
    return <Loading message="Gerando Folha-Resposta, por favor aguarde..." />;
  }

  if (
    (isCreating && isGeneratingDiagram) ||
    (isUpdating && isGeneratingDiagram)
  ) {
    return <Loading message="Salvando e Imprimindo..." />;
  }

  return (
    <form
      className="container my-4 no_print"
      onSubmit={(e) => e.preventDefault()}
      data-testid="candidate-form-container"
    >
      <ErrorMessages messages={errorMessages} clearErrors={clearErrorMessages} />
      
      <div className="card shadow" id="candidate-form" data-testid="candidate-form-card">
        <div className="card-body">
          <h4 className="mt-0 header-title fs-4 mb-3 text-dark" data-testid="form-title">
            Informações da Folha de Resposta
          </h4>
          <div className="row">
            {inputConfigs.map(({ id, label, placeholder }) => (
              <div className="col-md-6" key={id}>
                <InputField
                  id={id}
                  label={label}
                  placeholder={placeholder}
                  value={fields[id]}
                  onChange={handleChange}
                />
                {errors[id as keyof CandidateFields] && (
                  <div className="mt-1" data-testid={`error-${id}`}>
                    <small className="text-danger">{errors[id as keyof CandidateFields]}</small>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="row">
            <div className="col-md-6">
              <label htmlFor="personType" className="form-label" data-testid="label-personType">
                Tipo de Pessoa <Attention />
              </label>
              <select
                id="personType"
                name="personType"
                className="form-select"
                value={fields.personType}
                onChange={handleChange}
                data-testid="input-personType"
              >
                <option value="">Selecione</option>
                <option value="Aluno">Aluno</option>
                <option value="Candidato">Candidato</option>
              </select>
              {errors.personType && (
                <div className="mt-1" data-testid="error-personType">
                  <small className="text-danger">{errors.personType}</small>
                </div>
              )}
            </div>
          </div>
          <div className="d-flex justify-content-end mt-3 gap-2" data-testid="form-actions">
            {editMode ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdate}
                  disabled={isUpdating || isGeneratingDiagram}
                  data-testid="btn-update"
                >
                  {isUpdating || isGeneratingDiagram
                    ? "Atualizando..."
                    : "Atualizar Folha-Resposta"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleUpdateAndPrint}
                  disabled={isUpdating || isGeneratingDiagram}
                  data-testid="btn-update-print"
                >
                  {isUpdating || isGeneratingDiagram
                    ? "Atualizando..."
                    : "Atualizar e Imprimir Folha-Resposta"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isCreating || isGeneratingDiagram}
                  data-testid="btn-create"
                >
                  {isCreating || isGeneratingDiagram
                    ? "Criando..."
                    : "Criar Folha-Resposta"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSubmitAndPrint}
                  disabled={isCreating || isGeneratingDiagram}
                  data-testid="btn-create-print"
                >
                  {isCreating || isGeneratingDiagram
                    ? "Criando..."
                    : "Criar e Imprimir Folha-Resposta"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default CandidateForm;
